import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { fetchUsers, fetchUserAbsences } from "../../../../../api/user";
import { fetchEvents as fetchCalendarEvents } from "../../../../../api/events";
import { newReportAgreement } from "../../../../../api/service/report";
import { fetchContact } from "../../../../../api/customer/contact";
import { axiosWithToast, mapErrors } from "../../../../Utils";
import ReportAgreementFormColumn from "./ReportAgreementFormColumn";
import ReportAgreementWeekCalendar from "./ReportAgreementWeekCalendar";
import ReportAgreementNotifPanel from "./ReportAgreementNotifPanel";
import ReportAgreementModalFoot, {
  ReportAgreementModalHead,
} from "./ReportAgreementModalFoot";
import {
  addDays,
  absenceDaySet,
  appointmentToFormValues,
  availabilityWithAbsence,
  buildWeekScheduleBlocks,
  DAY_LABELS,
  DEFAULT_WORK_START_HOUR,
  formatWeekLabel,
  hasOverlap,
  startOfWeek,
  START_HOUR,
  toDateString,
  TOTAL_SLOTS,
  weekRangeParams,
} from "./schedule-utils";
import "./report-agreement-redesign.css";

const MOBILE_BREAKPOINT = 640;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return isMobile;
}

function defaultDayIndex(weekStart) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 7; i++) {
    const d = addDays(weekStart, i);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime()) return i;
  }
  return 0;
}

function MobileDayPicker({
  weekStart,
  weekLabel,
  selectedDay,
  onSelectDay,
  onWeekChange,
  onToday,
  absenceDays,
  t,
}) {
  return (
    <div className="ra-day-picker">
      <div className="ra-section-label">
        {t("report.agreement.redesign.step_day")}
      </div>
      <div className="ra-section-help">
        {t("report.agreement.redesign.day_help")}
      </div>

      <div className="ra-day-picker-nav">
        <button
          type="button"
          className="ra-icon-btn"
          onClick={() => onWeekChange(-1)}
          aria-label={t("report.agreement.redesign.prev_week")}
        >
          ‹
        </button>
        <button
          type="button"
          className="ra-icon-btn"
          onClick={onToday}
          style={{ borderColor: "var(--primary-200)", color: "var(--primary-700)" }}
        >
          {t("report.agreement.redesign.today")}
        </button>
        <button
          type="button"
          className="ra-icon-btn"
          onClick={() => onWeekChange(1)}
          aria-label={t("report.agreement.redesign.next_week")}
        >
          ›
        </button>
        <span className="ra-week-label">{weekLabel}</span>
      </div>

      <div className="ra-day-chip-grid" role="listbox" aria-label={t("report.agreement.redesign.step_day")}>
        {Array.from({ length: 7 }).map((_, i) => {
          const d = addDays(weekStart, i);
          const hasAbsence = absenceDays.includes(i);
          const isSelected = selectedDay === i;
          return (
            <button
              key={i}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={`ra-day-chip${isSelected ? " selected" : ""}${hasAbsence ? " absence" : ""}`}
              onClick={() => onSelectDay(i)}
            >
              <span className="dow">{DAY_LABELS[i]}</span>
              <span className="dnum">{d.getDate()}</span>
              {hasAbsence && (
                <span className="abs">
                  {t("report.agreement.redesign.absence_short")}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ReportAgreementNewFlow({ open, onClose, report, onRefresh }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isCompanyAdmin = Boolean(
    currentUser?.roles?.some((r) => r.name === "admin")
  );

  const [step, setStep] = useState("edit");
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedDay, setSelectedDay] = useState(() =>
    defaultDayIndex(startOfWeek(new Date()))
  );
  const [appointment, setAppointment] = useState(null);
  const [fixedTime, setFixedTime] = useState(false);
  const [requireConfirmation, setRequireConfirmation] = useState(true);
  const [existingEvents, setExistingEvents] = useState([]);
  const [absenceDays, setAbsenceDays] = useState(() => new Set());
  const [employeeAbsenceDays, setEmployeeAbsenceDays] = useState({});
  const [eventsLoading, setEventsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sendAgreementToCustomer, setSendAgreementToCustomer] = useState(true);
  const [sendSms, setSendSms] = useState(Boolean(report?.customer?.mobile));
  const [sendEmail, setSendEmail] = useState(Boolean(report?.customer?.email));
  const [phone, setPhone] = useState(report?.customer?.mobile || "");
  const [recipientDraft, setRecipientDraft] = useState("");
  const [email, setEmail] = useState(report?.customer?.email || "");
  const [projectContact, setProjectContact] = useState(null);
  const [serviceAgreementContact, setServiceAgreementContact] = useState(null);

  const mobileWizardSteps = useMemo(() => {
    const steps = [];
    if (isCompanyAdmin) {
      steps.push({
        id: "employee",
        labelKey: "report.agreement.redesign.step_employee",
      });
    }
    steps.push(
      { id: "schedule", labelKey: "report.agreement.redesign.step_day_time" },
      { id: "settings", labelKey: "report.agreement.redesign.step_settings" },
      { id: "notify", labelKey: "report.agreement.redesign.step_notify" }
    );
    return steps;
  }, [isCompanyAdmin]);

  const desktopWizardSteps = useMemo(
    () => [
      { id: "edit", labelKey: "report.agreement.redesign.step_schedule" },
      { id: "notify", labelKey: "report.agreement.redesign.step_notify" },
    ],
    []
  );

  const initialStep = useCallback(() => {
    if (isMobile) {
      return isCompanyAdmin ? "employee" : "schedule";
    }
    return "edit";
  }, [isMobile, isCompanyAdmin]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--slot-h",
      isMobile ? "18px" : "14px"
    );
  }, [isMobile]);

  useEffect(() => {
    if (!open) return;
    const ws = startOfWeek(new Date());
    setStep(initialStep());
    setSearch("");
    setAppointment(null);
    setFixedTime(false);
    setRequireConfirmation(true);
    setWeekStart(ws);
    setSelectedDay(defaultDayIndex(ws));
    setSendAgreementToCustomer(true);
    setSendSms(Boolean(report?.customer?.mobile));
    setSendEmail(Boolean(report?.customer?.email));
    setPhone(report?.customer?.mobile || "");
    setEmail(report?.customer?.email || "");
    setRecipientDraft("");

    const defaultExec = isCompanyAdmin
      ? report?.executing_employee?.id
      : currentUser?.id ?? report?.executing_employee?.id;
    setEmployeeId(defaultExec != null ? defaultExec : null);
  }, [open, report?.id, isCompanyAdmin, currentUser?.id, initialStep]);

  // Keep step coherent when viewport crosses the breakpoint mid-flow
  useEffect(() => {
    if (!open) return;
    if (isMobile && step === "edit") {
      setStep(isCompanyAdmin ? "employee" : "schedule");
    } else if (!isMobile && ["employee", "schedule", "settings"].includes(step)) {
      setStep("edit");
    }
  }, [isMobile, open, step, isCompanyAdmin]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEmployeesLoading(true);
    fetchUsers({
      page: 1,
      per_page: 50,
      sortBy: "name",
      sort: "asc",
      fields: "id,name,displayTitle,email",
      filters: [
        { name: "EMPLOYEE", value: true },
        ...(isCompanyAdmin ? [{ name: "role", value: "service" }] : []),
      ],
      ...(search.trim() ? { query: search.trim() } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        let list = Array.isArray(res?.data) ? res.data : [];
        if (!isCompanyAdmin && currentUser?.id) {
          list = list.filter((e) => String(e.id) === String(currentUser.id));
          if (list.length === 0 && currentUser) {
            list = [
              {
                id: currentUser.id,
                name: currentUser.name,
                displayTitle: currentUser.name || currentUser.email,
                email: currentUser.email,
              },
            ];
          }
        }
        setEmployees(list);
      })
      .catch(() => {
        if (!cancelled) setEmployees([]);
      })
      .finally(() => {
        if (!cancelled) setEmployeesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, search, isCompanyAdmin, currentUser]);

  useEffect(() => {
    if (!open || !report?.id) return;
    const loadContacts = async () => {
      if (report?.project?.contact?.id) {
        try {
          const response = await fetchContact(report.project.contact.id);
          if (response?.data) setProjectContact(response.data);
        } catch (_e) {
          setProjectContact(null);
        }
      } else {
        setProjectContact(null);
      }
      if (report?.agreement?.contact?.id) {
        try {
          const response = await fetchContact(report.agreement.contact.id);
          if (response?.data) setServiceAgreementContact(response.data);
        } catch (_e) {
          setServiceAgreementContact(null);
        }
      } else {
        setServiceAgreementContact(null);
      }
    };
    loadContacts();
  }, [open, report?.id, report?.project?.contact?.id, report?.agreement?.contact?.id]);

  useEffect(() => {
    if (!open || employees.length === 0) {
      setEmployeeAbsenceDays({});
      return;
    }
    let cancelled = false;
    const range = weekRangeParams(weekStart);
    Promise.all(
      employees.map((e) =>
        fetchUserAbsences(e.id, range)
          .then((res) => ({
            id: e.id,
            days: absenceDaySet(Array.isArray(res?.data) ? res.data : [], weekStart),
          }))
          .catch(() => ({ id: e.id, days: new Set() }))
      )
    ).then((rows) => {
      if (cancelled) return;
      const map = {};
      rows.forEach(({ id, days }) => {
        map[id] = days;
      });
      setEmployeeAbsenceDays(map);
    });
    return () => {
      cancelled = true;
    };
  }, [open, employees, weekStart]);

  useEffect(() => {
    if (!open || !employeeId) {
      setExistingEvents([]);
      setAbsenceDays(new Set());
      return;
    }
    let cancelled = false;
    setEventsLoading(true);
    const range = weekRangeParams(weekStart);
    Promise.all([
      fetchCalendarEvents({
        date: toDateString(weekStart),
        view: "week",
        users: String(employeeId),
      }),
      fetchUserAbsences(employeeId, range),
    ])
      .then(([eventsRes, absencesRes]) => {
        if (cancelled) return;
        const items = Array.isArray(eventsRes.data) ? eventsRes.data : [];
        const absences = Array.isArray(absencesRes?.data) ? absencesRes.data : [];
        const days = absenceDaySet(absences, weekStart);
        setAbsenceDays(days);
        setExistingEvents(
          buildWeekScheduleBlocks(items, absences, weekStart, t("absence"))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setExistingEvents([]);
          setAbsenceDays(new Set());
        }
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, employeeId, weekStart, t]);

  const visibleEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.displayTitle || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q)
    );
  }, [employees, search]);

  const employee = useMemo(
    () => employees.find((e) => String(e.id) === String(employeeId)),
    [employees, employeeId]
  );

  const weekEnd = addDays(weekStart, 6);
  const weekLabel = formatWeekLabel(weekStart, weekEnd);
  const absenceDayList = useMemo(() => [...absenceDays], [absenceDays]);

  const conflicts = useMemo(() => {
    if (!appointment) return [];
    const overlaps = existingEvents.filter(
      (ev) =>
        ev.day === appointment.day &&
        ev.kind !== "absence" &&
        hasOverlap(ev, appointment)
    );
    if (absenceDays.has(appointment.day)) {
      return [
        ...overlaps,
        {
          id: "absence-day",
          day: appointment.day,
          kind: "absence",
          startSlot: 0,
          endSlot: TOTAL_SLOTS,
          title: t("absence"),
        },
      ];
    }
    return overlaps;
  }, [appointment, existingEvents, absenceDays, t]);

  const availabilityFor = useCallback(
    (id) => {
      const days = employeeAbsenceDays[id] || new Set();
      const focusDay = String(id) === String(employeeId) ? appointment?.day : null;
      const count =
        String(id) === String(employeeId)
          ? existingEvents.filter((ev) => ev.kind !== "absence").length
          : 0;
      return availabilityWithAbsence({
        eventCount: count,
        absenceDays: days,
        focusDay,
      });
    },
    [employeeId, existingEvents, employeeAbsenceDays, appointment?.day]
  );

  const handleReceiverPick = (key) => {
    const pick = {
      customer: {
        email: report?.customer?.email,
        mobile: report?.customer?.mobile,
      },
      contact: {
        email: report?.contact?.email,
        mobile: report?.contact?.mobile,
      },
      project: {
        email: projectContact?.email || report?.project?.contact?.email,
        mobile: projectContact?.mobile || report?.project?.contact?.mobile,
      },
      agreement: {
        email:
          serviceAgreementContact?.email || report?.agreement?.contact?.email,
        mobile:
          serviceAgreementContact?.mobile || report?.agreement?.contact?.mobile,
      },
    }[key];
    if (!pick) return;
    if (pick.email) {
      setEmail(pick.email);
      setSendEmail(true);
    }
    if (pick.mobile) {
      setPhone(pick.mobile);
      setSendSms(true);
    }
  };

  const validateNotify = () => {
    if (!sendAgreementToCustomer) return true;
    if (sendSms && !phone?.trim()) {
      toast.error(t("report.agreement.validation.phone_for_sms"));
      return false;
    }
    if (sendEmail && recipientDraft.trim()) { toast.error(t("recipients.finishInput")); return false; }
    if (sendEmail && !Yup.string().trim().email().required().isValidSync(email)) {
      toast.error(t("documentationSend.invalid_email"));
      return false;
    }
    return true;
  };

  const handleSubmit = async ({ notify = true } = {}) => {
    if (!appointment || !employeeId || !report?.id) return;

    const shouldNotify = notify && sendAgreementToCustomer;
    if (shouldNotify && !validateNotify()) return;

    const formTimes = appointmentToFormValues(appointment, weekStart, fixedTime);
    if (!formTimes?.agreement_date) {
      toast.error(t("report.agreement.validation.date_required"));
      return;
    }

    const payload = {
      id: report.id,
      has_agreement: false,
      executing_employee: employeeId,
      fixedTime,
      appointmentConfirmationRequired: requireConfirmation,
      sendAgreementToCustomer: shouldNotify,
      sendSmsToCustomer: shouldNotify && sendSms,
      sendEmailToCustomer: shouldNotify && sendEmail,
      to_phone_number: phone || null,
      to_email_address: email || null,
      ...formTimes,
    };

    setIsSubmitting(true);
    try {
      await axiosWithToast(
        newReportAgreement(payload),
        (res) => onRefresh?.(res),
        () => {},
        () => {}
      );
      onClose();
    } catch (err) {
      const errors = err?.data?.errors ? mapErrors(err.data.errors) : null;
      if (errors?.length) toast.error(errors.join(", "));
      else toast.error(t("Something went wrong"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectDay = (dayIdx) => {
    setSelectedDay(dayIdx);
    if (appointment && appointment.day !== dayIdx) {
      setAppointment({ ...appointment, day: dayIdx });
    } else if (!appointment) {
      seedAppointmentForDay(dayIdx);
    }
  };

  const handleWeekChange = (dir) => {
    setWeekStart((prev) => {
      const next = addDays(prev, dir * 7);
      setSelectedDay(defaultDayIndex(next));
      setAppointment(null);
      return next;
    });
  };

  const handleToday = () => {
    const ws = startOfWeek(new Date());
    setWeekStart(ws);
    setSelectedDay(defaultDayIndex(ws));
    setAppointment(null);
  };

  const seedAppointmentForDay = (dayIdx) => {
    const startSlot = (DEFAULT_WORK_START_HOUR - START_HOUR) * 4;
    setAppointment({
      day: dayIdx,
      startSlot,
      endSlot: startSlot + 2,
    });
  };

  const ensureAppointmentForSelectedDay = () => {
    if (selectedDay == null) return false;
    if (!appointment || appointment.day !== selectedDay) {
      seedAppointmentForDay(selectedDay);
    }
    return true;
  };

  const goNextFrom = (current) => {
    if (current === "employee") {
      if (!employeeId) return;
      setStep("schedule");
      return;
    }
    if (current === "schedule") {
      if (!ensureAppointmentForSelectedDay()) return;
      setStep("settings");
      return;
    }
    if (current === "settings") {
      if (appointment && employee) setStep("notify");
      return;
    }
    if (current === "edit") {
      if (appointment && employee) setStep("notify");
    }
  };

  const goBackFrom = (current) => {
    if (current === "notify") {
      setStep(isMobile ? "settings" : "edit");
      return;
    }
    if (current === "settings") {
      setStep("schedule");
      return;
    }
    if (current === "schedule") {
      if (isCompanyAdmin) setStep("employee");
      else onClose();
      return;
    }
    if (current === "employee" || current === "edit") {
      onClose();
    }
  };

  const footPropsForStep = () => {
    const hasConflict = conflicts.length > 0;
    const base = {
      appointment,
      employee,
      weekStart,
      hasConflict,
      step,
      fixedTime,
      onClose,
      onSubmit: handleSubmit,
      isSubmitting,
    };

    if (!isMobile) {
      return {
        ...base,
        onBack: step === "notify" ? () => setStep("edit") : null,
        onNext: () => goNextFrom("edit"),
        nextDisabled: !appointment || !employee,
        nextLabel: t("report.agreement.redesign.next_notify"),
        showSummary: true,
      };
    }

    if (step === "employee") {
      return {
        ...base,
        onBack: null,
        onNext: () => goNextFrom("employee"),
        nextDisabled: !employeeId,
        nextLabel: t("report.agreement.redesign.next_day_time"),
        showSummary: false,
      };
    }
    if (step === "schedule") {
      return {
        ...base,
        onBack: () => goBackFrom("schedule"),
        onNext: () => goNextFrom("schedule"),
        nextDisabled: selectedDay == null || !employee,
        nextLabel: t("report.agreement.redesign.next_settings"),
        showSummary: true,
      };
    }
    if (step === "settings") {
      return {
        ...base,
        onBack: () => goBackFrom("settings"),
        onNext: () => goNextFrom("settings"),
        nextDisabled: !appointment || !employee,
        nextLabel: t("report.agreement.redesign.next_notify"),
        showSummary: true,
      };
    }
    return {
      ...base,
      onBack: () => goBackFrom("notify"),
      onNext: () => {},
      showSummary: true,
    };
  };

  const showDesktopLayout = !isMobile && (step === "edit" || step === "notify");
  const showMobileEmployee = isMobile && step === "employee";
  const showMobileSchedule = isMobile && step === "schedule";
  const showMobileSettings = isMobile && step === "settings";
  const showNotify = step === "notify";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[60] flex min-h-full items-center justify-center overflow-y-auto p-0 sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:scale-95"
          >
            <Dialog.Panel className="report-agreement-redesign mx-auto w-full max-w-[1400px] transform text-left align-middle transition-all">
              <div
                className={`ra-modal${isMobile ? " ra-modal--wizard" : ""}${
                  isMobile && step === "employee" ? " ra-modal--step-employee" : ""
                }${isMobile && step === "schedule" ? " ra-modal--step-schedule" : ""}${
                  isMobile && step === "settings" ? " ra-modal--step-settings" : ""
                }${isMobile && step === "notify" ? " ra-modal--step-notify" : ""}`}
              >
                <ReportAgreementModalHead
                  step={step}
                  onClose={onClose}
                  t={t}
                  wizardSteps={isMobile ? mobileWizardSteps : desktopWizardSteps}
                />

                {showDesktopLayout && (
                  <>
                    <ReportAgreementFormColumn
                      employees={visibleEmployees}
                      employeeId={employeeId}
                      setEmployeeId={setEmployeeId}
                      search={search}
                      setSearch={setSearch}
                      availabilityFor={availabilityFor}
                      showAvailability
                      appointment={appointment}
                      setAppointment={setAppointment}
                      weekStart={weekStart}
                      fixedTime={fixedTime}
                      setFixedTime={setFixedTime}
                      requireConfirmation={requireConfirmation}
                      setRequireConfirmation={setRequireConfirmation}
                      canPickEmployee={isCompanyAdmin}
                    />

                    <ReportAgreementWeekCalendar
                      employee={employee}
                      weekStart={weekStart}
                      weekLabel={weekLabel}
                      onWeekChange={(dir) => setWeekStart(addDays(weekStart, dir * 7))}
                      onToday={() => setWeekStart(startOfWeek(new Date()))}
                      appointment={appointment}
                      setAppointment={setAppointment}
                      existing={existingEvents}
                      absenceDays={absenceDayList}
                      showWeekend
                      isLoading={eventsLoading || employeesLoading}
                    />
                  </>
                )}

                {showMobileEmployee && (
                  <ReportAgreementFormColumn
                    className="ra-form-col--wizard"
                    sections={["employee"]}
                    employees={visibleEmployees}
                    employeeId={employeeId}
                    setEmployeeId={setEmployeeId}
                    search={search}
                    setSearch={setSearch}
                    availabilityFor={availabilityFor}
                    showAvailability={false}
                    appointment={appointment}
                    setAppointment={setAppointment}
                    weekStart={weekStart}
                    fixedTime={fixedTime}
                    setFixedTime={setFixedTime}
                    requireConfirmation={requireConfirmation}
                    setRequireConfirmation={setRequireConfirmation}
                    canPickEmployee={isCompanyAdmin}
                  />
                )}

                {showMobileSchedule && (
                  <div className="ra-schedule-step-body">
                    <MobileDayPicker
                      weekStart={weekStart}
                      weekLabel={weekLabel}
                      selectedDay={selectedDay}
                      onSelectDay={handleSelectDay}
                      onWeekChange={handleWeekChange}
                      onToday={handleToday}
                      absenceDays={absenceDayList}
                      t={t}
                    />
                    <ReportAgreementFormColumn
                      className="ra-form-col--wizard ra-form-col--schedule-meta"
                      sections={["schedule"]}
                      employees={visibleEmployees}
                      employeeId={employeeId}
                      setEmployeeId={setEmployeeId}
                      search={search}
                      setSearch={setSearch}
                      availabilityFor={availabilityFor}
                      showAvailability={false}
                      appointment={appointment}
                      setAppointment={setAppointment}
                      weekStart={weekStart}
                      fixedTime={fixedTime}
                      setFixedTime={setFixedTime}
                      requireConfirmation={requireConfirmation}
                      setRequireConfirmation={setRequireConfirmation}
                      canPickEmployee={false}
                      scheduleHelpKey="report.agreement.redesign.schedule_help_mobile"
                      dragHintKey="report.agreement.redesign.drag_hint_mobile"
                    />
                    <ReportAgreementWeekCalendar
                      employee={employee}
                      weekStart={weekStart}
                      weekLabel={weekLabel}
                      onWeekChange={handleWeekChange}
                      onToday={handleToday}
                      appointment={appointment}
                      setAppointment={setAppointment}
                      existing={existingEvents}
                      absenceDays={absenceDayList}
                      showWeekend
                      focusDay={selectedDay}
                      isLoading={eventsLoading || employeesLoading}
                    />
                  </div>
                )}

                {showMobileSettings && (
                  <ReportAgreementFormColumn
                    className="ra-form-col--wizard ra-form-col--settings-only"
                    sections={["settings"]}
                    employees={visibleEmployees}
                    employeeId={employeeId}
                    setEmployeeId={setEmployeeId}
                    search={search}
                    setSearch={setSearch}
                    availabilityFor={availabilityFor}
                    showAvailability={false}
                    appointment={appointment}
                    setAppointment={setAppointment}
                    weekStart={weekStart}
                    fixedTime={fixedTime}
                    setFixedTime={setFixedTime}
                    requireConfirmation={requireConfirmation}
                    setRequireConfirmation={setRequireConfirmation}
                    canPickEmployee={false}
                  />
                )}

                <ReportAgreementModalFoot {...footPropsForStep()} />

                <ReportAgreementNotifPanel
                  open={showNotify}
                  embedded={isMobile}
                  employee={employee}
                  appointment={appointment}
                  weekStart={weekStart}
                  report={report}
                  fixedTime={fixedTime}
                  requireConfirmation={requireConfirmation}
                  sendSms={sendSms}
                  setSendSms={setSendSms}
                  sendEmail={sendEmail}
                  setSendEmail={setSendEmail}
                  sendAgreementToCustomer={sendAgreementToCustomer}
                  setSendAgreementToCustomer={setSendAgreementToCustomer}
                  phone={phone}
                  setPhone={setPhone}
                  email={email}
                  setEmail={setEmail}
                  setRecipientDraft={setRecipientDraft}
                  onReceiverPick={handleReceiverPick}
                  onBack={() => goBackFrom("notify")}
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
